import { h } from '@omni-torn/ui';
import {
  ACTIVITY_BUCKETS,
  ACTIVITY_KEEP_DAYS,
  ACTIVITY_MEMBER_DAYS,
  ACTIVITY_MEMBERS_PATH,
  activeRatio,
  bestHours,
  daysFromWire,
  heatmap,
  hourProfile,
  hourText,
  memberActivity,
  memberActivityText,
  playerSeries,
  readActivityMembers,
  seenShare,
  WEEKDAYS,
  type ActivityBucket,
  type ActivityFactionAnswer,
  type ActivityMembersAnswer,
  type ActivityPlayerAnswer,
  type ActivitySeries,
  type HeatCell,
  type MemberActivity,
} from '@omni-torn/shared';
import { server } from '../session';
import { ago, box, ext, loading, num, problem, profileUrl, table, tabs, withTableCsv } from '../ui';

/**
 * Activity tracking & heatmaps (website roadmap S22 — FF Scouter's Activity Tracker, Torn Intel's activity and heatmaps),
 * drawn on the player page (lookup), a faction's profile and compare (factions) and your own faction (Faction → Activity).
 * Data: GET /api/activity/* — what Omni-Torn kept (five-minute slots, 90 days; terms 10): the faction watch (factions whose
 * members gave the Discord bot a key and agreed) and what Omni-Torn users saw of players. Nothing is read from Torn here.
 * Torn's time (TCT) is UTC: every hour and weekday below is TCT.
 */

const DAY = 86_400_000;
export const ACTIVITY_NOTE =
  'From Omni-Torn’s faction watch (factions whose members gave the Discord bot a key and agreed to terms 10, every 5 minutes) and what Omni-Torn users see of players — so it covers only part of the time; grey means not seen. Times are Torn time (TCT).';

// ---------------------------------------------------------------- reading (kept a minute while the page is open)

const asked = new Map<string, { at: number; p: Promise<unknown> }>();
function ask<T>(path: string): Promise<T> {
  const had = asked.get(path);
  if (had && Date.now() - had.at < 60_000) return had.p as Promise<T>;
  const p = server<T>('GET', path);
  asked.set(path, { at: Date.now(), p });
  p.catch(() => asked.delete(path));
  return p;
}
export const playerActivity = (id: number) => ask<ActivityPlayerAnswer>(`/api/activity/player/${id}`);
export const factionActivity = (id: number, days: number, bucket: ActivityBucket) => ask<ActivityFactionAnswer>(`/api/activity/faction/${id}?days=${days}&bucket=${bucket}`);
/** Your faction's members, 30 days (S20's roster reads the same: ACTIVITY_MEMBERS_PATH). */
export const membersActivity = () => ask<unknown>(ACTIVITY_MEMBERS_PATH).then((b) => readActivityMembers(b));

// ---------------------------------------------------------------- pieces

const pct = (x: number | null): string => (x === null ? '—' : `${Math.round(x * 100)}%`);
/** A heat level 0–5 for a share 0–1 (CSS classes: inline styles aren't allowed by the site's rules). */
const level = (share: number): number => (share <= 0 ? 0 : Math.min(5, 1 + Math.floor(share * 5)));

/**
 * Hour × weekday heatmap (TCT). `avg`: a faction — each cell says how many members were active on average in that hour
 * (and the share of those seen); else a player — the share of the hours seen they were active.
 */
export function heatTable(heat: HeatCell[][], opts: { avg?: boolean; label: string }): HTMLElement {
  const head = h('tr', {}, h('th', { scope: 'col', 'data-csv-value': 'TCT' }, ''), ...Array.from({ length: 24 }, (_, hr) => h('th', { scope: 'col', class: 'heat-h', 'data-csv-value': `${String(hr).padStart(2, '0')}:00` }, hr % 3 === 0 ? String(hr).padStart(2, '0') : '')));
  const rows = heat.map((row, d) =>
    h(
      'tr',
      {},
      h('th', { scope: 'row', class: 'heat-d' }, WEEKDAYS[d]!),
      ...row.map((c, hr) => {
        const avg = c.looks ? c.active / c.looks : 0;
        const what = c.share === null ? 'not seen enough' : opts.avg ? `${avg.toFixed(1)} members active on average · ${pct(c.share)} of those seen` : `active ${pct(c.share)} of the times seen`;
        return h('td', { class: c.share === null ? 'heat-c heat-none' : `heat-c heat-${level(c.share)}`, title: `${WEEKDAYS[d]} ${hourText(hr)}: ${what} (${c.looks} looks)`, 'data-csv-value': c.share === null ? '' : opts.avg ? avg.toFixed(1) : pct(c.share) }, '');
      }),
    ),
  );
  return withTableCsv(h('div', { class: 'table-wrap heat-wrap' }, h('table', { class: 'heat', 'aria-label': opts.label, 'data-activity': 'heatmap' }, h('thead', {}, head), h('tbody', {}, ...rows)), h('p', { class: 'muted small heat-key' }, 'less ', ...[0, 1, 2, 3, 4, 5].map((l) => h('span', { class: `heat-swatch heat-${l}` })), ' more · ', h('span', { class: 'heat-swatch heat-none' }), ' not seen enough')));
}

/** Buckets over time as bars (SVG): a player — green when active, grey when not seen; a faction — members active (and seen, faint). */
export function timeline(s: ActivitySeries, opts: { faction?: boolean; label: string }): HTMLElement {
  const n = s.active.length;
  if (!n) return h('p', { class: 'muted' }, 'Nothing yet.');
  const W = 600;
  const H = 90;
  const pad = { l: 28, r: 4, t: 6, b: 18 };
  const top = opts.faction ? Math.max(1, ...s.seen) : 1;
  const bw = (W - pad.l - pad.r) / n;
  const ns = 'http://www.w3.org/2000/svg';
  const el = (tag: string, attrs: Record<string, string | number>) => {
    const e = document.createElementNS(ns, tag);
    for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
    return e;
  };
  const svg = el('svg', { viewBox: `0 0 ${W} ${H}`, class: 'chart act-line', role: 'img', 'aria-label': opts.label, 'data-activity': 'timeline' });
  const Y = (v: number) => pad.t + (1 - v / top) * (H - pad.t - pad.b);
  const step = s.bucketMin * 60_000;
  for (let i = 0; i < n; i++) {
    const x = pad.l + i * bw;
    const at = new Date(s.from + i * step);
    const tip = `${at.toISOString().slice(5, 16).replace('T', ' ')} TCT`;
    if (!s.seen[i]) {
      svg.append(el('rect', { x, y: pad.t, width: Math.max(bw, 0.5), height: H - pad.t - pad.b, class: 'act-none' }));
      continue;
    }
    if (opts.faction) {
      const seen = el('rect', { x, y: Y(s.seen[i]!), width: Math.max(bw - 0.3, 0.5), height: H - pad.b - Y(s.seen[i]!), class: 'act-seen' });
      seen.append(Object.assign(document.createElementNS(ns, 'title'), { textContent: `${tip}: ${s.active[i]} of ${s.seen[i]} seen active` }));
      svg.append(seen);
    }
    if (s.active[i]) {
      const r = el('rect', { x, y: Y(s.active[i]!), width: Math.max(bw - 0.3, 0.5), height: H - pad.b - Y(s.active[i]!), class: 'act-on' });
      r.append(Object.assign(document.createElementNS(ns, 'title'), { textContent: opts.faction ? `${tip}: ${s.active[i]} active of ${s.seen[i]} seen` : `${tip}: active` }));
      svg.append(r);
    }
  }
  for (const [x, anchor, t] of [
    [pad.l, 'start', s.from],
    [W - pad.r, 'end', s.from + n * step],
  ] as const) {
    const tx = el('text', { x, y: H - 4, class: 'chart-axis', 'text-anchor': anchor });
    tx.textContent = new Date(t).toISOString().slice(5, 16).replace('T', ' ');
    svg.append(tx);
  }
  if (opts.faction) {
    const tx = el('text', { x: pad.l - 4, y: pad.t + 9, class: 'chart-axis', 'text-anchor': 'end' });
    tx.textContent = String(top);
    svg.append(tx);
  }
  return h('div', { class: 'chart-wrap' }, svg);
}

const bestLine = (heat: HeatCell[][], who: string): HTMLElement => {
  const best = bestHours(heat, 3);
  return h('p', { 'data-activity': 'best' }, h('strong', {}, 'Best hours to hit: '), best.length ? best.map((b) => `${hourText(b.hour)} (${who} active ${pct(b.share)})`).join(' · ') : h('span', { class: 'muted' }, 'not enough seen yet.'));
};

const RANGES = [
  { id: '1', label: '24 hours' },
  { id: '7', label: '7 days' },
];
const BUCKET_TABS = ACTIVITY_BUCKETS.map((b) => ({ id: String(b), label: `${b} min` }));

// ---------------------------------------------------------------- one player

/** "When they play" on the player page: their 30-day heatmap, best hours, and the last day or week in 5/15/60-minute buckets. */
export async function playerActivityBox(id: number): Promise<HTMLElement> {
  let a: ActivityPlayerAnswer;
  try {
    a = await playerActivity(id);
  } catch (err) {
    return box('When they play', problem(err), { id: 'player-activity-heat' });
  }
  const days = daysFromWire(a.days);
  if (!days.size) return box('When they play', [h('p', { class: 'muted' }, 'Omni-Torn hasn’t seen when they play yet.'), h('p', { class: 'muted small' }, ACTIVITY_NOTE)], { id: 'player-activity-heat' });
  const now = Date.now();
  const m = memberActivity(id, days, now, ACTIVITY_MEMBER_DAYS);
  const heat = heatmap(playerSeries(days, now - ACTIVITY_MEMBER_DAYS * DAY, now, 60));
  const line = h('div', {});
  let range = '1';
  let bucket: ActivityBucket = 15;
  const drawLine = () => line.replaceChildren(timeline(playerSeries(days, now - Number(range) * DAY, now, bucket), { label: `Active in the last ${range === '1' ? 'day' : 'week'}` }));
  drawLine();
  return box(
    'When they play',
    [
      h('p', { 'data-activity': 'summary' }, `Last ${ACTIVITY_MEMBER_DAYS} days: ${memberActivityText(m)}`, m.lastActive ? ` · last seen active ${ago(m.lastActive)}` : '', h('span', { class: 'muted' }, ` · seen ${pct(m.coverage)} of the time`)),
      bestLine(heat, 'they were'),
      heatTable(heat, { label: 'When they’re active, by weekday and hour (TCT)' }),
      h('div', { class: 'act-controls' }, tabs(RANGES, range, (r) => ((range = r), drawLine())), tabs(BUCKET_TABS, String(bucket), (b) => ((bucket = Number(b) as ActivityBucket), drawLine()))),
      line,
      h('p', { class: 'muted small' }, ACTIVITY_NOTE, a.keepDays === 0 ? ' Kept with no end date.' : ` Kept ${a.keepDays || ACTIVITY_KEEP_DAYS} days.`),
    ],
    { id: 'player-activity-heat' },
  );
}

// ---------------------------------------------------------------- a faction

/** A faction's activity: 30-day heatmap (average members active), active ratio, best hours, the last day or week. */
export async function factionActivityBox(factionId: number, title = 'When they play'): Promise<HTMLElement> {
  let a: ActivityFactionAnswer;
  try {
    a = await factionActivity(factionId, 30, 60);
  } catch (err) {
    return box(title, problem(err), { id: 'faction-activity' });
  }
  const watchedWord = a.watched ? 'Omni-Torn’s faction watch reads this faction every 5 minutes.' : 'Not watched: only what Omni-Torn users saw of its members.';
  if (!a.covered) return box(title, [h('p', { class: 'muted' }, `Omni-Torn hasn’t seen when its members play yet. ${watchedWord}`), h('p', { class: 'muted small' }, ACTIVITY_NOTE)], { id: 'faction-activity' });
  const heat = heatmap(a.series);
  const line = h('div', {}, loading());
  let range = '1';
  let bucket: ActivityBucket = 15;
  const drawLine = async () => {
    try {
      const r = await factionActivity(factionId, Number(range), bucket);
      line.replaceChildren(timeline(r.series, { faction: true, label: `Members active in the last ${range === '1' ? 'day' : 'week'}` }));
    } catch (err) {
      line.replaceChildren(problem(err));
    }
  };
  void drawLine();
  return box(
    title,
    [
      h(
        'p',
        { 'data-activity': 'summary' },
        h('strong', {}, `Active ratio ${pct(activeRatio(a.series))}`),
        ` of member-hours seen in the last ${a.days} days · ${num(a.covered)} of ${num(a.members)} members seen · seen ${pct(seenShare(a.series))} of the hours`,
      ),
      bestLine(heat, 'members'),
      heatTable(heat, { avg: true, label: 'Members active, by weekday and hour (TCT)' }),
      h('div', { class: 'act-controls' }, tabs(RANGES, range, (r) => ((range = r), void drawLine())), tabs(BUCKET_TABS, String(bucket), (b) => ((bucket = Number(b) as ActivityBucket), void drawLine()))),
      line,
      h('p', { class: 'muted small' }, watchedWord, ' ', ACTIVITY_NOTE),
    ],
    { id: 'faction-activity' },
  );
}

/** Hour of day (TCT), two factions: the share of members seen who were active (grouped bars). */
function hourPair(a: HeatCell[], b: HeatCell[], names: [string, string]): HTMLElement {
  const W = 600;
  const H = 120;
  const pad = { l: 30, r: 4, t: 6, b: 18 };
  const top = Math.max(0.05, ...a.map((c) => c.share ?? 0), ...b.map((c) => c.share ?? 0));
  const gw = (W - pad.l - pad.r) / 24;
  const ns = 'http://www.w3.org/2000/svg';
  const el = (tag: string, attrs: Record<string, string | number>) => {
    const e = document.createElementNS(ns, tag);
    for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
    return e;
  };
  const svg = el('svg', { viewBox: `0 0 ${W} ${H}`, class: 'chart', role: 'img', 'aria-label': `Active by hour (TCT): ${names[0]} and ${names[1]}`, 'data-activity': 'compare-hours' });
  const Y = (v: number) => pad.t + (1 - v / top) * (H - pad.t - pad.b);
  for (let hr = 0; hr < 24; hr++) {
    for (const [k, c] of [
      [0, a[hr]!],
      [1, b[hr]!],
    ] as const) {
      if (c.share === null) continue;
      const r = el('rect', { x: pad.l + hr * gw + k * (gw / 2) + 0.5, y: Y(c.share), width: Math.max(gw / 2 - 1, 0.5), height: H - pad.b - Y(c.share), class: k ? 'act-b' : 'act-a' });
      r.append(Object.assign(document.createElementNS(ns, 'title'), { textContent: `${names[k]} ${hourText(hr)}: ${pct(c.share)} of members seen were active` }));
      svg.append(r);
    }
    if (hr % 3 === 0) {
      const t = el('text', { x: pad.l + hr * gw + gw / 2, y: H - 4, class: 'chart-axis', 'text-anchor': 'middle' });
      t.textContent = String(hr).padStart(2, '0');
      svg.append(t);
    }
  }
  const t = el('text', { x: pad.l - 4, y: pad.t + 9, class: 'chart-axis', 'text-anchor': 'end' });
  t.textContent = pct(top);
  svg.append(t);
  return h('div', { class: 'chart-wrap' }, svg);
}

/** Two factions' activity side by side (Factions → compare). */
export async function compareActivityBox(a: { id: number; name: string }, b: { id: number; name: string }): Promise<HTMLElement> {
  const [ra, rb] = await Promise.all([factionActivity(a.id, 30, 60).catch((e: Error) => e), factionActivity(b.id, 30, 60).catch((e: Error) => e)]);
  if (ra instanceof Error || rb instanceof Error) return box('When they play', problem(ra instanceof Error ? ra : rb), { id: 'faction-compare-activity' });
  if (!ra.covered && !rb.covered) return box('When they play', [h('p', { class: 'muted' }, 'Omni-Torn hasn’t seen when either faction’s members play yet.'), h('p', { class: 'muted small' }, ACTIVITY_NOTE)], { id: 'faction-compare-activity' });
  const ha = heatmap(ra.series);
  const hb = heatmap(rb.series);
  const best = (x: HeatCell[][]) => bestHours(x, 3).map((h0) => `${String(h0.hour).padStart(2, '0')}:00`).join(', ') || '—';
  const rows: Array<[string, string, string]> = [
    ['Active ratio (30 days)', pct(activeRatio(ra.series)), pct(activeRatio(rb.series))],
    ['Members seen', `${ra.covered} of ${ra.members}`, `${rb.covered} of ${rb.members}`],
    ['Hours seen', pct(seenShare(ra.series)), pct(seenShare(rb.series))],
    ['Quietest hours (TCT)', best(ha), best(hb)],
  ];
  return box(
    'When they play',
    [
      withTableCsv(h(
        'div',
        { class: 'table-wrap' },
        h(
          'table',
          { 'data-activity': 'compare' },
          h('thead', {}, h('tr', {}, h('th', { scope: 'col' }, ''), h('th', { scope: 'col', class: 'r' }, a.name), h('th', { scope: 'col', class: 'r' }, b.name))),
          h('tbody', {}, ...rows.map(([l, x, y]) => h('tr', {}, h('th', { scope: 'row' }, l), h('td', { class: 'r' }, x), h('td', { class: 'r' }, y)))),
        ),
      )),
      h('p', { class: 'small' }, h('span', { class: 'swatch' }), ` ${a.name}   `, h('span', { class: 'swatch swatch-b' }), ` ${b.name} — share of members seen who were active, by hour (TCT)`),
      hourPair(hourProfile(ha), hourProfile(hb), [a.name, b.name]),
      h('p', { class: 'muted small' }, ACTIVITY_NOTE),
    ],
    { id: 'faction-compare-activity' },
  );
}

// ---------------------------------------------------------------- your faction's members

/** A tiny 24-hour strip (TCT): how often they were active in each hour. */
function hourStrip(hours: Array<number | null>): HTMLElement {
  return h('span', { class: 'hour-strip', title: hours.map((x, i) => `${String(i).padStart(2, '0')}: ${x === null ? 'not seen' : pct(x)}`).join('\n') }, ...hours.map((x) => h('span', { class: x === null ? 'heat-none' : `heat-${level(x)}` })));
}

/** Faction → Activity: your members' 30-day activity (the same numbers S20's roster shows) and the faction's heatmap. */
export async function membersActivityBox(names: ReadonlyMap<number, string>, factionId: number): Promise<HTMLElement[]> {
  let a: ActivityMembersAnswer | null;
  try {
    a = await membersActivity();
  } catch (err) {
    return [box(`Members’ activity (${ACTIVITY_MEMBER_DAYS} days)`, problem(err), { id: 'faction-members-activity' })];
  }
  const rows: MemberActivity[] = a?.members ?? [];
  const named = (id: number) => names.get(id) ?? `[${id}]`;
  const watched = a?.watched
    ? h('p', { class: 'muted small' }, 'Omni-Torn’s faction watch reads your faction every 5 minutes (a member’s Discord-bot key; that member agreed to terms 10).')
    : h('p', { class: 'notice' }, 'Your faction isn’t watched yet: a member gives the Discord bot a key (the extension’s options or the website’s Account → Your faction) and agrees to terms version 10 — then this fills in every 5 minutes.');
  const list = box(
    `Members’ activity (${ACTIVITY_MEMBER_DAYS} days)`,
    [
      watched,
      table(
        rows,
        [
          { label: 'Member', get: (m) => h('span', {}, h('a', { href: `/lookup/${m.id}`, 'data-link': true }, named(m.id)), ' ', ext(profileUrl(m.id), '↗')), sort: (m) => named(m.id).toLowerCase() },
          { label: 'Active a day', get: (m) => (m.hoursPerDay === null ? h('span', { class: 'muted' }, '—') : `${m.hoursPerDay.toFixed(1)} h`), sort: (m) => m.hoursPerDay ?? -1, cls: 'r' },
          { label: 'Days active', get: (m) => `${m.activeDays} of ${ACTIVITY_MEMBER_DAYS}`, sort: (m) => m.activeDays, cls: 'r' },
          { label: 'Last active', get: (m) => (m.lastActive ? ago(m.lastActive) : '—'), sort: (m) => m.lastActive ?? 0 },
          { label: 'Seen', get: (m) => pct(m.coverage), sort: (m) => m.coverage, cls: 'r' },
          { label: 'By hour (TCT)', get: (m) => hourStrip(m.hours) },
        ],
        { initial: 1, desc: true, empty: 'No members known yet.', remember: 'faction-activity' },
      ),
      h('p', { class: 'muted small' }, 'Active a day: the share of the time seen they were active, over a whole day. ', ACTIVITY_NOTE),
    ],
    { id: 'faction-members-activity' },
  );
  return [list, await factionActivityBox(factionId, 'When your faction plays')];
}
